import type { SubmitRequestUnion } from "@nlc/shared-types";
import type { UseFormReturn } from "react-hook-form";

import {
  BriefCreatorTypeEnum,
  isCustomerRole,
  RequestTypeEnum,
  submitRequestUnionZod,
} from "@nlc/shared-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { useIsRequestFormDirty } from "@/hooks/useIsRequestFormDirty";
import { useUser } from "@/hooks/useUser";
import authenticatedApi from "@/lib/api/client";

import type { CreateWebsiteImplementationRequestFormData } from "../components/useCreateWebsiteImplementationRequestForm";

import { useNavigateToRequestList } from "../../shared/useNavigateToRequestList";
import { getValidAttachmentIds } from "../../shared/utils";

type UseSubmitMutationProps = {
  draftId: number | undefined;
  formMethods: UseFormReturn<CreateWebsiteImplementationRequestFormData>;
};

export function useSubmitMutation({
  draftId,
  formMethods,
}: UseSubmitMutationProps) {
  const { data: user } = useUser();
  const resetFormDirty = useIsRequestFormDirty((state) => state.reset);
  const navigateToRequestList = useNavigateToRequestList();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      formData: CreateWebsiteImplementationRequestFormData,
    ) => {
      if (!draftId) throw new Error("No request ID found");

      const body: SubmitRequestUnion = {
        attachmentIds: [
          ...getValidAttachmentIds(formData.assets),
          ...getValidAttachmentIds(formData.inspirations),
        ],
        brandId: formData.brandId ?? undefined,
        briefCreatorType: BriefCreatorTypeEnum.SINGLE,
        collaboratorIds: formData.collaboratorIds,
        complexityId: formData.designComplexityId,
        description: formData.requestDescriptionHTML,
        designTypeId: formData.designTypeId,
        name: formData.requestName,
        preferredDesignerIds: formData.preferredDesignerIds,
        requestType: RequestTypeEnum.WEBSITE_IMPLEMENTATION,
      };
      const parsed = submitRequestUnionZod.parse(body);

      return authenticatedApi
        .url(`/requests/${draftId}/submit`)
        .post(parsed)
        .json<{ message: string; redirectTo?: string; requestId?: number }>();
    },
    onError: (error: { message: string; statusCode: number }) => {
      console.error("Failed to submit request:", error);

      if (error.message.includes("Request name already exists")) {
        formMethods.setError("requestName", {
          message: "Request name already exists",
        });
        formMethods.setFocus("requestName");
      }

      if (error.statusCode === 400) {
        const errorMessage = error.message;

        if (
          [
            "No credits available",
            "Subscription ended",
            "Subscription is cancelled",
          ].includes(errorMessage)
        ) {
          navigateToRequestList();

          return;
        }
      }

      // Handle kickstarter account trying to submit
      if (error.statusCode === 403) {
        toast.add(
          {
            description: error.message,
            title: "Cannot submit request",
            type: "error",
          },
          {
            timeout: 8000,
          },
        );

        return;
      }

      toast.add(
        {
          description: error.message,
          title: "Error submitting request",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    },
    onMutate: () => {
      formMethods.reset(formMethods.getValues());
      resetFormDirty();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });

      // Check if user has customer role (owner, admin, collaborator)
      const isCustomer = user && isCustomerRole(user.user.roleId);

      navigateToRequestList({ showProcessing: !!isCustomer });
    },
  });
}
